import { useState, useEffect, createContext, useContext } from "react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "sb_cookie_consent";
const CONSENT_VERSION = 1;
const CONSENT_EXPIRY_DAYS = 365;

const DEFAULT_PREFS = {
  essential: true,   // always true, non-negotiable
  analytics: false,
  marketing: false,
};

function loadConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (saved.version !== CONSENT_VERSION) return null;
    const expires = new Date(saved.expires);
    if (expires < new Date()) { localStorage.removeItem(STORAGE_KEY); return null; }
    return saved;
  } catch {
    return null;
  }
}

function saveConsent(prefs) {
  const expires = new Date();
  expires.setDate(expires.getDate() + CONSENT_EXPIRY_DAYS);
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    version: CONSENT_VERSION,
    expires: expires.toISOString(),
    prefs,
  }));
}

// ─── Context ─────────────────────────────────────────────────────────────────

const CookieConsentContext = createContext({ prefs: DEFAULT_PREFS, hasConsented: false });

export function useCookieConsent() {
  return useContext(CookieConsentContext);
}

// ─── Toggle Switch ────────────────────────────────────────────────────────────

function Toggle({ checked, onChange, disabled, id }) {
  return (
    <button
      id={id}
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        flexShrink: 0,
        width: "44px", height: "24px",
        borderRadius: "99px",
        border: "none",
        cursor: disabled ? "default" : "pointer",
        background: checked ? "var(--gold)" : "rgba(255,255,255,0.15)",
        position: "relative",
        transition: "background 0.25s",
        padding: 0,
        outline: "none",
      }}
      onFocus={e => { if (!disabled) e.currentTarget.style.boxShadow = "0 0 0 3px rgba(212,168,83,0.4)"; }}
      onBlur={e => { e.currentTarget.style.boxShadow = "none"; }}
    >
      <span style={{
        position: "absolute",
        top: "3px",
        left: checked ? "23px" : "3px",
        width: "18px", height: "18px",
        borderRadius: "50%",
        background: disabled ? "rgba(255,255,255,0.4)" : "var(--white)",
        transition: "left 0.25s",
        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
      }} />
    </button>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CookieBanner({ children }) {
  const saved = loadConsent();
  const [hasConsented, setHasConsented] = useState(!!saved);
  const [prefs, setPrefs] = useState(saved?.prefs ?? DEFAULT_PREFS);
  const [showBanner, setShowBanner] = useState(!saved);
  const [showModal, setShowModal] = useState(false);
  // draft prefs while modal is open
  const [draft, setDraft] = useState(prefs);

  // sync context on mount if already consented
  useEffect(() => {
    if (saved) setPrefs(saved.prefs);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function acceptAll() {
    const p = { essential: true, analytics: true, marketing: true };
    setPrefs(p); saveConsent(p);
    setHasConsented(true); setShowBanner(false); setShowModal(false);
  }

  function rejectNonEssential() {
    const p = { essential: true, analytics: false, marketing: false };
    setPrefs(p); saveConsent(p);
    setHasConsented(true); setShowBanner(false); setShowModal(false);
  }

  function savePreferences() {
    const p = { ...draft, essential: true };
    setPrefs(p); saveConsent(p);
    setHasConsented(true); setShowBanner(false); setShowModal(false);
  }

  function openModal() {
    setDraft(prefs);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
  }

  // trap focus inside modal when open
  useEffect(() => {
    if (!showModal) return;
    function onKey(e) {
      if (e.key === "Escape") closeModal();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showModal]);

  // prevent body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = showModal ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [showModal]);

  const categories = [
    {
      key: "essential",
      label: "Strictly Necessary",
      tag: "Always Active",
      locked: true,
      desc: "These cookies are required for the website to function. They cannot be disabled. They include session management and security tokens.",
    },
    {
      key: "analytics",
      label: "Analytics",
      tag: "Optional",
      locked: false,
      desc: "Help us understand how visitors use our site by collecting anonymous usage data. We may use tools such as Google Analytics.",
    },
    {
      key: "marketing",
      label: "Marketing",
      tag: "Optional",
      locked: false,
      desc: "Used to build a profile of your interests and show relevant content. Only set with your consent.",
    },
  ];

  return (
    <CookieConsentContext.Provider value={{ prefs, hasConsented }}>
      {children}

      {/* ── Banner ─────────────────────────────────────────────────────────── */}
      {showBanner && !showModal && (
        <div
          role="dialog"
          aria-modal="false"
          aria-label="Cookie consent"
          style={{
            position: "fixed", bottom: 0, left: 0, right: 0,
            zIndex: 9997,
            background: "var(--night)",
            borderTop: "1px solid rgba(212,168,83,0.25)",
            boxShadow: "0 -4px 32px rgba(0,0,0,0.45)",
            padding: "20px clamp(16px, 5vw, 40px)",
          }}
        >
          <div style={{
            maxWidth: "1100px", margin: "0 auto",
            display: "flex", flexWrap: "wrap",
            alignItems: "center", gap: "16px",
          }}>
            {/* Text */}
            <div style={{ flex: "1 1 300px", minWidth: 0 }}>
              <p style={{
                margin: 0, color: "rgba(255,255,255,0.85)",
                fontSize: "0.88rem", lineHeight: 1.6,
              }}>
                We use cookies to improve your experience and understand how our site is used.{" "}
                <Link
                  to="/cookies"
                  style={{ color: "var(--gold)", textDecoration: "underline" }}
                  onClick={() => setShowBanner(false)}
                >
                  Cookie Policy
                </Link>
              </p>
            </div>

            {/* Buttons */}
            <div style={{
              display: "flex", flexWrap: "wrap",
              gap: "10px", alignItems: "center",
              flexShrink: 0,
            }}>
              <button
                onClick={openModal}
                style={{
                  background: "none",
                  border: "1px solid rgba(255,255,255,0.25)",
                  borderRadius: "var(--radius-sm, 6px)",
                  color: "rgba(255,255,255,0.7)",
                  fontFamily: "var(--font-body)",
                  fontSize: "0.83rem",
                  padding: "8px 16px",
                  cursor: "pointer",
                  transition: "border-color 0.2s, color 0.2s",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--gold)"; e.currentTarget.style.color = "var(--gold)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
              >
                Cookie Settings
              </button>
              <button
                onClick={rejectNonEssential}
                style={{
                  background: "none",
                  border: "1px solid rgba(255,255,255,0.25)",
                  borderRadius: "var(--radius-sm, 6px)",
                  color: "rgba(255,255,255,0.7)",
                  fontFamily: "var(--font-body)",
                  fontSize: "0.83rem",
                  padding: "8px 16px",
                  cursor: "pointer",
                  transition: "border-color 0.2s, color 0.2s",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)"; e.currentTarget.style.color = "rgba(255,255,255,1)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
              >
                Reject Non-Essential
              </button>
              <button
                onClick={acceptAll}
                style={{
                  background: "var(--gold)",
                  border: "1px solid var(--gold)",
                  borderRadius: "var(--radius-sm, 6px)",
                  color: "var(--night)",
                  fontFamily: "var(--font-body)",
                  fontWeight: 600,
                  fontSize: "0.83rem",
                  padding: "8px 20px",
                  cursor: "pointer",
                  transition: "opacity 0.2s",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = "0.88"; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Preferences Modal ───────────────────────────────────────────────── */}
      {showModal && (
        <>
          {/* Backdrop */}
          <div
            onClick={closeModal}
            style={{
              position: "fixed", inset: 0,
              zIndex: 9998,
              background: "rgba(10,5,12,0.75)",
              backdropFilter: "blur(3px)",
            }}
          />

          {/* Modal */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Cookie preferences"
            style={{
              position: "fixed",
              top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10000,
              width: "min(560px, calc(100vw - 32px))",
              maxHeight: "calc(100vh - 64px)",
              overflowY: "auto",
              background: "var(--night)",
              border: "1px solid rgba(212,168,83,0.2)",
              borderRadius: "var(--radius-lg, 16px)",
              boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
              padding: "32px",
            }}
          >
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
              <h2 style={{
                fontFamily: "var(--font-display)",
                color: "var(--white)",
                fontSize: "1.25rem",
                fontWeight: 700,
                margin: 0,
              }}>
                Cookie Preferences
              </h2>
              <button
                onClick={closeModal}
                aria-label="Close cookie preferences"
                style={{
                  background: "none", border: "none",
                  color: "rgba(255,255,255,0.5)",
                  cursor: "pointer", fontSize: "1.4rem",
                  lineHeight: 1, padding: "2px 4px",
                  transition: "color 0.2s",
                  marginTop: "-2px",
                }}
                onMouseEnter={e => { e.currentTarget.style.color = "var(--white)"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "rgba(255,255,255,0.5)"; }}
              >
                ×
              </button>
            </div>

            <p style={{
              color: "rgba(255,255,255,0.6)",
              fontSize: "0.88rem",
              lineHeight: 1.65,
              margin: "0 0 24px",
            }}>
              Choose which cookies you allow. Strictly necessary cookies are always active.{" "}
              <Link to="/cookies" style={{ color: "var(--gold)" }} onClick={closeModal}>
                Learn more
              </Link>
            </p>

            {/* Categories */}
            <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
              {categories.map((cat, i) => (
                <div
                  key={cat.key}
                  style={{
                    padding: "20px 0",
                    borderTop: i === 0 ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(255,255,255,0.08)",
                    borderBottom: i === categories.length - 1 ? "1px solid rgba(255,255,255,0.08)" : "none",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px", marginBottom: "8px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <span style={{
                        fontFamily: "var(--font-body)",
                        fontWeight: 600,
                        fontSize: "0.92rem",
                        color: "var(--white)",
                      }}>
                        {cat.label}
                      </span>
                      <span style={{
                        display: "inline-block",
                        fontSize: "0.68rem",
                        fontWeight: 600,
                        fontFamily: "var(--font-body)",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        padding: "2px 8px",
                        borderRadius: "99px",
                        background: cat.locked ? "rgba(212,168,83,0.18)" : "rgba(199,125,138,0.15)",
                        color: cat.locked ? "var(--gold)" : "rgba(255,255,255,0.6)",
                      }}>
                        {cat.tag}
                      </span>
                    </div>
                    <Toggle
                      id={`toggle-${cat.key}`}
                      checked={cat.locked ? true : draft[cat.key]}
                      onChange={val => setDraft(d => ({ ...d, [cat.key]: val }))}
                      disabled={cat.locked}
                    />
                  </div>
                  <p style={{
                    margin: 0,
                    color: "rgba(255,255,255,0.5)",
                    fontSize: "0.83rem",
                    lineHeight: 1.6,
                  }}>
                    {cat.desc}
                  </p>
                </div>
              ))}
            </div>

            {/* Modal actions */}
            <div style={{
              display: "flex", flexWrap: "wrap",
              gap: "10px", marginTop: "24px",
              justifyContent: "flex-end",
            }}>
              <button
                onClick={rejectNonEssential}
                style={{
                  background: "none",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "var(--radius-sm, 6px)",
                  color: "rgba(255,255,255,0.6)",
                  fontFamily: "var(--font-body)",
                  fontSize: "0.83rem",
                  padding: "9px 16px",
                  cursor: "pointer",
                  transition: "border-color 0.2s, color 0.2s",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.5)"; e.currentTarget.style.color = "var(--white)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; e.currentTarget.style.color = "rgba(255,255,255,0.6)"; }}
              >
                Reject Non-Essential
              </button>
              <button
                onClick={savePreferences}
                style={{
                  background: "none",
                  border: "1px solid var(--gold)",
                  borderRadius: "var(--radius-sm, 6px)",
                  color: "var(--gold)",
                  fontFamily: "var(--font-body)",
                  fontWeight: 600,
                  fontSize: "0.83rem",
                  padding: "9px 16px",
                  cursor: "pointer",
                  transition: "background 0.2s, color 0.2s",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(212,168,83,0.12)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
              >
                Save My Preferences
              </button>
              <button
                onClick={acceptAll}
                style={{
                  background: "var(--gold)",
                  border: "1px solid var(--gold)",
                  borderRadius: "var(--radius-sm, 6px)",
                  color: "var(--night)",
                  fontFamily: "var(--font-body)",
                  fontWeight: 600,
                  fontSize: "0.83rem",
                  padding: "9px 20px",
                  cursor: "pointer",
                  transition: "opacity 0.2s",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = "0.88"; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
              >
                Accept All
              </button>
            </div>
          </div>
        </>
      )}
    </CookieConsentContext.Provider>
  );
}
